import React from 'react';

import { useQuery } from '@tanstack/react-query';

import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, Chip, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridPagination } from '@mui/x-data-grid';

import CorporateEventsService, { Registration } from '~/services/CorporateEventsService';
import { formatDateSlashes } from '~/services/TimeService';

interface RegistrationsListProps {
  eventId?: string;
  eventName?: string;
}

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  submitted: 'success',
  pending: 'warning',
  cancelled: 'error',
};

function exportToCsv(registrations: Array<Registration>, eventName?: string) {
  const headers = [
    'First Name',
    'Middle Name',
    'Last Name',
    'Email',
    'Status',
    'Date of Birth',
    'Mobile Prefix',
    'Mobile Number',
    'Nationality',
    'Passport Number',
    'Passport Expiry Date',
    'Additional Details',
    'Will Extend Trip',
    'Extension Notes',
    'Will Bring Guest',
    'Guest Details',
    'Preferred Airline',
    'Preferred Seat Class',
    'Submitted At',
    'Last Updated',
    'Invited User ID',
    'Custom Answers',
  ];

  const rows = registrations.map((r) => [
    r.firstName,
    r.middleName ?? '',
    r.lastName,
    r.email ?? '',
    r.status,
    formatDateSlashes(r.dateOfBirth),
    r.mobilePrefix ?? '',
    r.mobileNumber ?? '',
    r.nationality ?? '',
    r.passportNumber ?? '',
    formatDateSlashes(r.passportExpiryDate),
    r.additionalDetails ?? '',
    r.willExtendTrip != null ? String(r.willExtendTrip) : '',
    r.extensionNotes ?? '',
    r.willBringGuest != null ? String(r.willBringGuest) : '',
    r.guestDetails ?? '',
    r.preferredAirline ?? '',
    r.preferredSeatClass ?? '',
    formatDateSlashes(r.firstSubmittedAt),
    formatDateSlashes(r.lastUpdatedAt),
    r.invitedUserId ?? '',
    r.customAnswers && r.customAnswers.length > 0 ? JSON.stringify(r.customAnswers) : '',
  ]);

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const slug = eventName ? eventName.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'event';
  const filename = `${slug}-registrations.csv`;

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function RegistrationsList({ eventId, eventName }: RegistrationsListProps) {
  const {
    data: registrations = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['corporate-events', 'registrations', eventId],
    queryFn: () => {
      if (!eventId) {
        return Promise.resolve([]);
      }
      return CorporateEventsService.getRegistrationsByEventId(eventId);
    },
    enabled: !!eventId,
  });

  const columns: Array<GridColDef<Registration>> = [
    {
      field: 'firstName',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => [row.firstName, row.middleName, row.lastName].filter(Boolean).join(' '),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={STATUS_COLORS[params.value] ?? 'default'}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'mobileNumber',
      headerName: 'Mobile',
      width: 160,
      valueGetter: (value, row) => {
        if (!row.mobileNumber) {
          return '';
        }
        return row.mobilePrefix ? `${row.mobilePrefix} ${row.mobileNumber}` : row.mobileNumber;
      },
    },
    {
      field: 'firstSubmittedAt',
      headerName: 'Submitted At',
      width: 130,
      valueFormatter: (value) => formatDateSlashes(value),
    },
    {
      field: 'lastUpdatedAt',
      headerName: 'Last Updated',
      width: 130,
      valueFormatter: (value) => formatDateSlashes(value),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => exportToCsv(registrations, eventName)}
          disabled={registrations.length === 0}
        >
          Export Registrations
        </Button>
      </Stack>

      {error && (
        <Box mb={2} p={2} sx={{ backgroundColor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          Error loading registrations: {error instanceof Error ? error.message : 'Unknown error'}
        </Box>
      )}

      <DataGrid
        loading={isLoading}
        rows={registrations}
        columns={columns}
        pagination
        initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
        pageSizeOptions={[10, 25, 50, 100]}
        getRowId={(row) => row.id}
        slots={{ pagination: GridPagination }}
        autoHeight
        disableRowSelectionOnClick
      />
    </Box>
  );
}
